import { useTranslation } from 'react-i18next';
import {
  Text,
  Button,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  Link,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  MoneyRegular,
  ArrowSyncRegular,
  CheckmarkCircleRegular,
  WarningRegular,
  OpenRegular,
} from '@fluentui/react-icons';
import { useClientBilling, useRefreshClientBilling } from '../../services/stateTaxClientsApi';
import { formatDate, formatCurrency, formatWholeDay } from '../../utils/complianceFormatters';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  heading: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  section: { marginTop: tokens.spacingVerticalM },
  sectionLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalXS,
  },
  muted: { color: tokens.colorNeutralForeground3, fontStyle: 'italic', fontSize: tokens.fontSizeBase200 },
  synced: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  reconRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: '2px 0',
    fontSize: tokens.fontSizeBase300,
  },
  billed: { color: tokens.colorPaletteGreenForeground1 },
  unbilled: { color: tokens.colorPaletteRedForeground1 },
  refRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '4px' },
});

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const map: Record<string, { color: 'success' | 'danger' | 'warning' | 'informative' | 'subtle'; key: string }> = {
    reconciled: { color: 'success', key: 'reconciled' },
    gaps: { color: 'danger', key: 'gaps' },
    review: { color: 'warning', key: 'review' },
    no_recurring: { color: 'subtle', key: 'noRecurring' },
    not_linked: { color: 'subtle', key: 'notLinked' },
  };
  const m = map[status] ?? map.no_recurring;
  return <Badge appearance="tint" color={m.color}>{t(`clientDetailPage.billing.status.${m.key}`)}</Badge>;
}

export function BillingCard({ stateTaxClientId }: { stateTaxClientId: string }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const dash = t('common.dash');
  const { data, isLoading, isError } = useClientBilling(stateTaxClientId);
  const refresh = useRefreshClientBilling();

  const onRefresh = () => refresh.mutate(stateTaxClientId);

  return (
    <div className="card">
      <div className={styles.header}>
        <Text className={styles.heading}>
          <MoneyRegular style={{ marginRight: 6, verticalAlign: '-3px' }} />
          {t('clientDetailPage.billing.title')}
        </Text>
        <Button
          appearance="subtle"
          size="small"
          icon={refresh.isPending ? <Spinner size="tiny" /> : <ArrowSyncRegular />}
          disabled={refresh.isPending}
          onClick={onRefresh}
        >
          {t('clientDetailPage.billing.refresh')}
        </Button>
      </div>

      {isLoading && (
        <div style={{ padding: '12px 0' }}>
          <Spinner size="tiny" label={t('common.loading')} />
        </div>
      )}

      {isError && (
        <MessageBar intent="error" style={{ marginTop: 8 }}>
          <MessageBarBody>{t('clientDetailPage.billing.loadError')}</MessageBarBody>
        </MessageBar>
      )}

      {!isLoading && !isError && !data && (
        <Text className={styles.muted} block style={{ padding: '12px 0' }}>
          {t('clientDetailPage.billing.noSnapshot')}
        </Text>
      )}

      {!isLoading && !isError && data && (
        <>
          {!data.xeroLinked ? (
            <MessageBar intent="info">
              <MessageBarBody>{t('clientDetailPage.billing.notLinked')}</MessageBarBody>
            </MessageBar>
          ) : (
            <>
              {data.syncError && (
                <MessageBar intent="warning" style={{ marginBottom: 8 }}>
                  <MessageBarBody>{t('clientDetailPage.billing.syncError')}</MessageBarBody>
                </MessageBar>
              )}

              {/* Recurring status */}
              <div className={styles.section}>
                <div className={styles.sectionLabel}>{t('clientDetailPage.billing.recurringLabel')}</div>
                {data.recurringActive ? (
                  data.recurring.map((r) => (
                    <div key={r.repeatingInvoiceId} className={styles.refRow}>
                      <Badge appearance="tint" color="success">{t('clientDetailPage.billing.active')}</Badge>
                      {r.reference && <Text weight="semibold">{r.reference}</Text>}
                      {typeof r.total === 'number' && <Text>{formatCurrency(r.total)}</Text>}
                      {r.scheduleUnit && (
                        <Text className={styles.muted}>
                          {t('clientDetailPage.billing.schedule', {
                            period: r.schedulePeriod ?? 1,
                            unit: (r.scheduleUnit ?? '').toLowerCase(),
                          })}
                        </Text>
                      )}
                      {r.nextScheduledDate && (
                        <Text className={styles.muted}>
                          {t('clientDetailPage.billing.nextOn', { date: formatWholeDay(r.nextScheduledDate) })}
                        </Text>
                      )}
                    </div>
                  ))
                ) : (
                  <Text className={styles.unbilled}>{t('clientDetailPage.billing.noRecurring')}</Text>
                )}
              </div>

              {/* Per-obligation reconciliation */}
              {data.reconciliation.perObligation.length > 0 && (
                <div className={styles.section}>
                  <div className={styles.sectionLabel}>
                    {t('clientDetailPage.billing.reconciliationLabel')} <StatusBadge status={data.reconciliation.status} />
                  </div>
                  {data.reconciliation.perObligation.map((p) => (
                    <div key={p.taxType} className={styles.reconRow}>
                      {p.billed ? (
                        <CheckmarkCircleRegular className={styles.billed} />
                      ) : (
                        <WarningRegular className={styles.unbilled} />
                      )}
                      <span>{p.taxType}</span>
                      {p.billed ? (
                        p.matchedVia && <span className={styles.muted}>({p.matchedVia})</span>
                      ) : (
                        <span className={styles.unbilled}>{t('clientDetailPage.billing.notBilled')}</span>
                      )}
                    </div>
                  ))}
                  {data.reconciliation.bookkeepingBilled && (
                    <Text className={styles.muted} block style={{ marginTop: 4 }}>
                      {t('clientDetailPage.billing.bookkeepingBilled')}
                    </Text>
                  )}
                  {data.reconciliation.unparsedTokens.length > 0 && (
                    <Text className={styles.muted} block style={{ marginTop: 4 }}>
                      {t('clientDetailPage.billing.unparsed', { tokens: data.reconciliation.unparsedTokens.join(', ') })}
                    </Text>
                  )}
                </div>
              )}

              {/* Overdue invoices */}
              <div className={styles.section}>
                <div className={styles.sectionLabel}>
                  {t('clientDetailPage.billing.overdueLabel')}{' '}
                  {data.overdue.count > 0 && (
                    <Badge appearance="tint" color="danger">
                      {t('clientDetailPage.billing.overdueCount', {
                        count: data.overdue.count,
                        amount: formatCurrency(data.overdue.totalAmountDue),
                      })}
                    </Badge>
                  )}
                </div>
                {data.overdueInvoices.length === 0 ? (
                  <Text className={styles.billed}>{t('clientDetailPage.billing.noOverdue')}</Text>
                ) : (
                  <Table size="small" aria-label={t('clientDetailPage.billing.overdueLabel')}>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell>{t('clientDetailPage.billing.cols.invoice')}</TableHeaderCell>
                        <TableHeaderCell>{t('clientDetailPage.billing.cols.due')}</TableHeaderCell>
                        <TableHeaderCell>{t('clientDetailPage.billing.cols.daysOverdue')}</TableHeaderCell>
                        <TableHeaderCell>{t('clientDetailPage.billing.cols.amountDue')}</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.overdueInvoices.map((o) => (
                        <TableRow key={o.invoiceId}>
                          <TableCell>
                            <TableCellLayout>
                              {o.xeroUrl ? (
                                <Link href={o.xeroUrl} target="_blank" rel="noreferrer">
                                  {o.invoiceNumber ?? o.invoiceId} <OpenRegular fontSize={12} />
                                </Link>
                              ) : (
                                o.invoiceNumber ?? o.invoiceId
                              )}
                            </TableCellLayout>
                          </TableCell>
                          <TableCell><TableCellLayout>{o.dueDate ? formatWholeDay(o.dueDate) : dash}</TableCellLayout></TableCell>
                          <TableCell><TableCellLayout>{o.daysOverdue}</TableCellLayout></TableCell>
                          <TableCell><TableCellLayout>{typeof o.amountDue === 'number' ? formatCurrency(o.amountDue) : dash}</TableCellLayout></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              <Text className={styles.synced} block style={{ marginTop: 12 }}>
                {t('clientDetailPage.billing.syncedAt', { date: formatDate(data.syncedAt) })}
              </Text>
            </>
          )}
        </>
      )}
    </div>
  );
}
